import React, {useEffect, useState, useTransition} from "react";
import {Controller, useForm} from "react-hook-form";

import * as specificationApi from "../../api/equipmentApi/specificationApi";
import * as Params from "../../models/clientModels/interfaces/apiParams";

import {
  ResponseFile,
  ResponseList,
  ResponseRemove,
} from "../../models/clientModels/interfaces/Response";
import {
  EQSpecification,
  EquipmentListParams,
  initialEquipmentListParams,
} from "../../models/serverModels/equipment/EQSpecification";

import {typeValue} from "../../models/clientModels/data/selectList";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  styled,
  TextField,
} from "@mui/material";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import MWSkeleton from "../../components/MWSkeleton";
import {useCreateMutation, useGetQuery, useRemoveMutation} from "../../hooks/useQueryHooks";

import {useQueryClient} from "react-query";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import ActionButtonGroup from "../../components/ActionButtonGroup";
import MWAlert from "../../components/MWAlert";
import SubTitle from "../../components/SubTitle";
import {equipmentListActionButton} from "../../models/clientModels/data/actionButtonData";
import {
  adminEquipmentListTableHead,
  createEquipmentListTableHead,
} from "../../models/clientModels/data/tableHeaderData";
import {alertStateAtom, extraAlertStateAtom} from "../../Recoil/Atom/AlertAtom";
import {levelSelector, tokenSelector} from "../../Recoil/Atom/authState";
import {FilterGroup, SubMenuGroup} from "../../styledComponents/StyledListContainer";
import {PaginationContainer} from "../../styledComponents/StyledPagination";
import {ReactComponent as ExcelIcon} from "../../assets/images/excel.svg";
import {StyledTableContainer, StyledTableHead} from "../../styledComponents/StyledTable";
import {equipmentFilterAtom} from "../../Recoil/Atom/filterState";

// 스타일링

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1700px",
}));

const EquipmentList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") || 1);

  const tokenState = useRecoilValue(tokenSelector);
  const setAlertState = useSetRecoilState(alertStateAtom);
  const userLevel = useRecoilValue(levelSelector);

  const [extraAlertState, setExtraAlertState] = useRecoilState(extraAlertStateAtom);

  // const [page, setPage] = useState<number>(1);

  // const [filter, setFilter] = useState<EquipmentListParams>({
  //   ...initialEquipmentListParams,
  //   token: tokenState,
  // });

  const [filter, setFilter] = useRecoilState(equipmentFilterAtom);

  const [isPending, startTransition] = useTransition();

  const {data, isLoading, isError, isFetching} = useGetQuery<ResponseList<EQSpecification>>({
    key: ["EquipmentList", {...filter, token: tokenState, page}],
    api: () => specificationApi.getList({...filter, token: tokenState, page}),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: specificationApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("EquipmentList");
      },
    },
  });

  const {mutate: loadMutate} = useCreateMutation<ResponseFile, Params.ImportFile>({
    api: specificationApi.importEquipments,
    options: {
      onSuccess: (data: ResponseFile) => {
        setExtraAlertState({...extraAlertState, toggle: true, message: data.extra});
      },
      onError: (error: Error) => {
        setExtraAlertState({...extraAlertState, toggle: true, message: error?.message});
      },
    },
  });

  const {control, reset, setValue} = useForm<EquipmentListParams>({
    defaultValues: initialEquipmentListParams,
  });

  const selectChangeHandler = async (event: SelectChangeEvent<any>) => {
    startTransition(() => {
      const {name, value} = event.target;
      setValue(name as keyof EquipmentListParams, value);
      setFilter({...filter, page: 1, [name]: value});
    });
  };

  const equipmentFilteringHandler = (
    event: React.BaseSyntheticEvent<Event, EventTarget & HTMLInputElement>
  ) => {
    const {name, value} = event.target;
    setValue(name as keyof EquipmentListParams, value);
    setFilter({...filter, page: 1, [name]: value});
  };

  const resetHandler = () => {
    setFilter(initialEquipmentListParams);
    reset();
  };

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilter({...filter, page: value});
    setSearchParams({page: value.toString()}); // URL에 반영
    // const currentState = window.history.state || {};
    // window.history.replaceState(
    //   {...currentState, page: value},
    //   "",
    //   window.location.pathname + window.location.search
    // );
  };

  // useEffect(() => {
  //   const historyState = window.history.state;
  //   console.log("⚾ ~ useEffect ~ historyState:", historyState);
  //   if (historyState && historyState.page) {
  //     setPage(historyState.page);
  //     setFilter({...filter, page: historyState.page});
  //   }
  // }, []);

  useEffect(() => {
    reset();
  }, []);

  if (isError) {
    return <MWAlert />;
  }
  return (
    <ListContainer>
      <SubTitle subTitle="설비이력카드 목록" />
      <SubMenuGroup>
        <FilterGroup>
          <Box>
            <Controller
              name="type"
              control={control}
              render={({field}) => (
                <FormControl>
                  <InputLabel id="typeLabel">설비분류</InputLabel>
                  <Select
                    {...field}
                    labelId="typeLabel"
                    id="type"
                    label="설비분류"
                    size="small"
                    value={filter?.type}
                    onChange={selectChangeHandler}
                  >
                    {typeValue.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
          <Box>
            <Controller
              name="identifyNumber"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="설비번호"
                  size="small"
                  value={filter?.identifyNumber}
                  onChange={equipmentFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="name"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="설비명"
                  size="small"
                  value={filter?.name}
                  onChange={equipmentFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="level"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="설비등급"
                  size="small"
                  value={filter?.level}
                  onChange={equipmentFilteringHandler}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="creator"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  label="작성자"
                  size="small"
                  value={filter?.creator}
                  onChange={equipmentFilteringHandler}
                />
              )}
            />
          </Box>
          <Button
            onClick={resetHandler}
            variant="contained"
            color="success"
            size="small"
            sx={{whiteSpace: "nowrap"}}
          >
            초기화
          </Button>
        </FilterGroup>
        <ActionButtonGroup data={equipmentListActionButton(tokenState)}>
          <Button
            variant="outlined"
            size="medium"
            component="label"
            startIcon={<ExcelIcon width="20px" height="20px" />}
            color="success"
            sx={{whiteSpace: "nowrap", flexShrink: 0, fontSize: "12px"}}
          >
            Excel 불러오기
            <TextField
              sx={{display: "none"}}
              onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                const convertBase64 = (data: Blob): Promise<any> => {
                  return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(data);
                    reader.onload = () => {
                      resolve(reader.result);
                    };
                    reader.onerror = (error) => {
                      reject(error);
                    };
                  });
                };
                const file = event.target?.files![0];
                const base64 = await convertBase64(file);
                loadMutate({token: tokenState, base64: base64.split(",")[1]});
              }}
              type="file"
              id="base64"
              inputProps={{
                accept:
                  ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel",
              }}
            />
          </Button>
        </ActionButtonGroup>
      </SubMenuGroup>

      {/* 설비이력카드목록 테이블 */}
      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              {(userLevel < 4 ? createEquipmentListTableHead : adminEquipmentListTableHead).map(
                (item, index) => (
                  <TableCell key={index} align="center">
                    {item}
                  </TableCell>
                )
              )}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={12} />
            ) : (
              data?.items.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">
                    {page === 1
                      ? index + 1
                      : (page - 1) * initialEquipmentListParams.rows! + index + 1}
                  </TableCell>
                  <TableCell align="center">
                    {typeValue.map((v) => (v.value === item.type ? v.label : ""))}
                  </TableCell>
                  <TableCell align="center">{item.identifyNumber}</TableCell>
                  <TableCell align="center">{item.eqLevel}</TableCell>
                  <TableCell align="center">
                    <Link to={`detail/${item.uid}`}>{item.name}</Link>
                  </TableCell>
                  <TableCell align="center">{item.manufacture}</TableCell>
                  <TableCell align="center">{item.modelNo}</TableCell>
                  <TableCell align="center">{item.serialNo}</TableCell>
                  {/* <TableCell align="center">
                    <img
                      src={process.env.REACT_APP_PATH_EQ_PICTURE + item.picture}
                      alt=""
                      width="80px"
                    />
                  </TableCell> */}
                  <TableCell align="center">{item.officerName}</TableCell>
                  <TableCell align="center">{`${new Date(item?.entryDate).getFullYear()}년 ${
                    new Date(item?.entryDate).getMonth() + 1
                  }월 ${new Date(item?.entryDate).getDate()}일`}</TableCell>
                  <TableCell align="center">{item.creatorName}</TableCell>

                  {/* 사용자 등급 메뉴 */}
                  {userLevel < 4 ? (
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        sx={{marginRight: "8px"}}
                        onClick={() => {
                          navigate(`Edit/${item.uid}`, {
                            state: item.uid,
                          });
                        }}
                      >
                        편집
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        color="secondary"
                        onClick={() => {
                          setAlertState({
                            toggle: true,
                            message: "설비이력카드를 삭제하시겠습니까?",
                            execution: () => removeMutate({token: tokenState, id: item.uid}),
                          });
                        }}
                      >
                        삭제
                      </Button>
                    </TableCell>
                  ) : (
                    userLevel < 4 && (
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            navigate(`/WorkOrder/Create/${item.uid}`, {
                              state: item.uid,
                            });
                          }}
                        >
                          설비이력작성
                        </Button>
                      </TableCell>
                    )
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={data?.maxPage}
          page={page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default EquipmentList;
